import angular from 'angular'
import { CurrentOrganization } from '../../../components/current-organization'
import { CurrentUser } from '../../../components/current-user'
import { buildSplitClient } from './splitio'
import {
  vanillaFeatureFlags,
  vanillaSplitClient,
  vanillaUserAndOrganizationStore
} from 'contrastBase/state'
import { SCM_ONBOARDING_ROUTE_PATH } from '../../../components/route'
import { Role } from '@contrast-security-inc/base-types'
import { normalizeOrganization } from '../../../components/organization-licenses/api/requests/get-organization'
import { userHasResourceAction } from 'contrastBase/accessControl'

export interface UiApplicationControllerScope {
  $on: angular.IScope['$on']
  $watch: angular.IScope['$watch']
  footerStyle: { bottom: '0px'; position: 'fixed' } | { position: 'relative' }
  isSuperAdmin: boolean
  isEmbeddedInNS: any
  canApplyDarkTheme: any
  defaultStickyPadding: any
  isHarmonyEnabled: any
  isSastEnabled: any
  isCloudnativeEnabled: any
  showReactComponentFromCookie: any
  isRbacServiceOn: any
  isRbacServiceEnforced: any
  showUsersAndGroups: any
  showMFABanner: any
  showAccessControl: any
  showAuditLog: any
  isRbacSsoOn: any
  showRbacSsoDisplaySettings: any
  showLegacyAuditLogUi: any
  showLegacyPermissionUi: any
  showOrganizationSettingsPage: any
  showOrganizationSettingsLink: any
  showOrganizationViewSettings: any
  showReportsDashboard: any
  updateIsNavigatingToNewUi: any
  getIsNavigatingToNewUi: any
  showNavigateToNewUi: any
  isAppConfigViewEnabled: any
  hasEnterpriseProtectAccessPermission: (eacRole: Role) => boolean
  hasProtectAccessPermission: (eacRole: Role) => boolean
  hasExclusionsPermission: (appId: string) => boolean
  hasOrganizationExclusionsPermission: (eacRole: Role) => boolean
  hasPoliciesPermission: (eacRole: Role, appId: string) => boolean
  hasOrganizationPoliciesPermission: () => boolean
  hasSensitiveDataPermission: () => boolean
  allowEnableImpersonation: boolean
  isFeatureFlagOn: (flagName: string) => boolean
  hideScaDataUnlessOrgAdmin: () => boolean
  main: {
    footer: any
    header: any
    name: any
    timeFormat: any
    dateFormat: any
    dateTimeFormat: any
    timezone: any
    uid: any
    id: any
    managingUsers: any
    head: any
    featureFlags: any
    agentFeatures: any
    currentOrg: any
    orgName: any
    timezoneOffset: string
  }
  bridgeApi: {
    currentOrganization: CurrentOrganization | undefined
    currentUser: CurrentUser | undefined
    isUserSessionActive: () => boolean
    getCurrentOrganization: () => CurrentOrganization | undefined
    getCurrentUser: () => CurrentUser | undefined
  }
  admin: {
    fixedHeader: any
    fixedSidebar: any
  }
  showSetupHeader: () => any
  hideHeader: () => any
  showHeader: () => any
  showFooter: () => any
  readonly showAddAgentButton: () => boolean
  readonly hasPermissionShowAddButton: () => boolean
  readonly showAddNewSensorButton: () => boolean
  isDiagnosticsEnabled?: boolean
  areTreatmentsLoaded?: boolean
  areUserActionsLoaded?: boolean | null
  isOrgBeingChanged?: boolean
  isNavigatingToNewUi?: boolean
  calculateStickyTop: (value: number) => number
}

export function AppCtrl(
  $scope: UiApplicationControllerScope,
  $rootScope,
  $cookies,
  $window: {
    readonly history: angular.IWindowService['history']
    location: angular.IWindowService['location']
    canApplyDarkTheme: boolean
    isEmbeddedInNS: boolean
  },
  $sce: {
    readonly trustAsHtml: angular.ISCEService['trustAsHtml']
  },
  permissions: LegacyPermissionsService,
  CommonFactory,
  UserService: LegacyUserService,
  ProgressBarService,
  ErrorService: LegacyErrorService,
  GlobalService,
  ProfileService,
  FeaturesService,
  ProfileFactory,
  AuthService,
  GridToggleService,
  HistoryService,
  TimeService,
  TIMEOUT_EVENT,
  AUTH_EVENTS,
  ACCESS_EVENTS,
  ORGANIZATION_SWITCH_EVENT,
  TS_BASE_URL,
  $http,
  $log,
  $location
) {
  'ngInject'

  let _authenticated = false

  function init() {
    loadCustomSettings()
    _authenticated = AuthService.isAuthenticated()
    $scope.$on('changeFooterBottom', (_, params) => {
      $scope.footerStyle = params.shouldSendToBottom
        ? { bottom: '0px', position: 'fixed' }
        : { position: 'relative' }
    })
  }

  /**
   * Other controllers will inherit this and should not overwrite it.
   */
  angular.extend($scope, {
    progressbarType: ProgressBarService.getType,
    maxPermission: permissions.maxPermission,
    checkPermission: permissions.hasPermission,
    checkEnterprisePermission: permissions.hasEnterprisePermission,
    checkAnyPermission: permissions.hasAnyPermission,
    checkAllPermissions: permissions.hasAllPermission,
    setPermissions: permissions.setPermissions,
    showReactComponentFromCookie,

    isAuthenticated: () => _authenticated,
    areTreatmentsLoaded: false,
    areUserActionsLoaded: false,
    isSaaS: permissions.isSaaS,
    isEOP: permissions.isEOP,
    isRASPEnabled: permissions.isRASPEnabled,
    isServerlessEnabled: permissions.isServerlessEnabled,
    isFreemium: permissions.isFreemium,
    showSetupHeader: permissions.showSetupHeader,
    isAttackEventsViewEnabled: permissions.isAttackEventsViewEnabled,
    isOrgProtectionEnabled: UserService.isOrganizationProtectionEnabled,
    isEnterprise: UserService.isEnterprise,
    isSuperAdmin: false,
    isEmbeddedInNS,
    canApplyDarkTheme,
    defaultStickyPadding,
    isHarmonyEnabled: false,
    isSastEnabled: false,
    isCloudnativeEnabled: false,
    isDiagnosticsEnabled: false,
    isRbacServiceOn,
    isRbacServiceEnforced,
    showUsersAndGroups,
    showMFABanner,
    getSecurityStandardReportStatus,
    showAccessControl,
    showAuditLog,
    isRbacSsoOn,
    showRbacSsoDisplaySettings,
    showLegacyAuditLogUi,
    showLegacyPermissionUi,
    showOrganizationSettingsPage,
    showOrganizationSettingsLink,
    showOrganizationViewSettings,
    showReportsDashboard,
    updateIsNavigatingToNewUi,
    getIsNavigatingToNewUi,
    showNavigateToNewUi,
    isAppConfigViewEnabled,
    hasEnterpriseProtectAccessPermission,
    hasProtectAccessPermission,
    hasExclusionsPermission,
    hasOrganizationExclusionsPermission,
    hasPoliciesPermission,
    hasOrganizationPoliciesPermission,
    hasSensitiveDataPermission,
    isFeatureFlagOn,
    hideScaDataUnlessOrgAdmin,
    isScanMetaDataFlagEnabled,
    isOrgBeingChanged: false,

    toggleOldGridOn: GridToggleService.toggleOldGridOn,
    toggleOldGridOff: GridToggleService.toggleOldGridOff,
    isOldGridToggledOn: GridToggleService.isOldGridToggledOn,

    mainAppModel: {
      getAgentFeatures: () => FeaturesService.getAll()
    },

    main: {
      brand: 'Contrast Security',
      featureFlags: [],
      getRouteHistory: () => HistoryService.getHistory()
    },
    admin: {
      layout: 'wide',
      menu: 'horizontal',
      fixedHeader: true,
      fixedSidebar: false
    },

    color: {
      primary: '#1BB7A0',
      success: '#94B758',
      info: '#56BDF1',
      infoAlt: '#7F6EC7',
      warning: '#F3C536',
      danger: '#FA7B58'
    },
    footerStyle: undefined,
    // the bridgeApi namespace exists to provide a common set of often used data to react bridges
    // see ANGULAR_TO_REACT_BRIDGES.md for more information
    bridgeApi: {
      getCurrentUser: mapProfileToUser,
      getCurrentOrganization,
      isUserSessionActive: () => _authenticated,
      currentOrganization: undefined,
      currentUser: undefined
    }
  })

  angular.extend($scope, {
    hideHeader: () => $location.path().includes(SCM_ONBOARDING_ROUTE_PATH),
    showFooter: () =>
      $scope.main.footer ? $scope.main.footer.length > 0 : false,
    showHeader: () =>
      $scope.main.header ? $scope.main.header.length > 0 : false,
    goBack: () => $window.history.back(),
    toTrusted: (htmlCode) => $sce.trustAsHtml(htmlCode),
    calculateStickyTop: (top) => {
      if ($rootScope.isEmbeddedInNS) {
        return 0
      }

      if ($scope.showSetupHeader()) {
        top = top + 30
      }

      if ($scope.showHeader()) {
        top = top + (permissions.isEOP() ? 24 : 75)
      }

      return top
    },
    calculateStickyBottom: (bottom) => {
      if ($scope.showFooter()) {
        bottom = bottom + 150
      }

      return bottom
    },
    hasPermissionShowAddButton: () =>
      permissions.isRbacNewSensorButtonEnabled() ||
      permissions.hasPermission('EDIT'),
    showAddNewSensorButton: () =>
      $scope.main.currentOrg &&
      (permissions.isServerlessEnabled() ||
        $scope.isHarmonyEnabled ||
        ($scope.isFeatureFlagOn('PROD-2175_GitHub_App_Repo_Table') &&
          $scope.isFeatureFlagOn('PROD-2340_SIR_Projects_Tab'))), // Wizard has all 4 cards, only one that supports the Add Repo card
    showAddAgentButton: () =>
      // Simple Add Agent dialog, not a wizard
      $scope.main.currentOrg && !$scope.showAddNewSensorButton()
  })

  function mapProfileToUser(options?) {
    const { userActions, userAccessObj } = options || {}
    return {
      name: ProfileService.getFirstName(),
      uid: ProfileService.getUid(),
      id: ProfileService.getId(),
      dateTimeFormat: ProfileService.getDateTimeFormat(),
      locale: ProfileService.getLanguage(),
      newUserNotificationPreference:
        ProfileService.getNewUserNotificationPreference(),
      setNewUserOnboardingPreferenceCallback:
        ProfileService.setNewUserOnboardingPreference,
      roles: userActions
        ? userActions
        : (permissions
            .getPermissions()
            .map((role) => role.slice('ROLE_'.length)) as Role[]),
      accessList: userAccessObj || [],
      isProtectEnabled: permissions.isRASPEnabled()
    }
  }

  function isEmbeddedInNS() {
    try {
      if (window.top && window.top !== window.self) {
        const windowTopIsNorthstar = window.top.location.href.includes(
          '/Contrast/cs/index.html#/'
        )
        const isEmbeddedInNS =
          window.top.location.origin === window.location.origin &&
          windowTopIsNorthstar
        $rootScope.isEmbeddedInNS = isEmbeddedInNS
        $window.isEmbeddedInNS = isEmbeddedInNS
        return isEmbeddedInNS
      }
    } catch (error) {
      console.error('Error checking origin:', error)
    }
    return false
  }

  function canApplyDarkTheme() {
    const {
      currentOrganization: { organizationUuid }
    } = vanillaUserAndOrganizationStore.getState()
    const darkThemeApprovedUrls = [
      '/account/profile',
      '/account/security-settings',
      '/settings/organization',
      '/settings/security/policy',
      '/settings/sso',
      '/settings/server',
      '/settings/application',
      '/rules/manage',
      '/rules/controls',
      '/rules/vulnerability-management',
      '/rules/protection',
      '/rules/cve',
      '/rules/patches',
      '/rules/log',
      '/rules/ip',
      '/rules/sensitive',
      '/applications',
      '/servers'
    ]

    const isDarkThemeApprovedUrl = darkThemeApprovedUrls.some((url) => {
      // Check if the URL contains the organization UUID and the path
      return window.location.href.includes(`${organizationUuid}${url}`)
    })

    // Check if user has set a dark theme preference in localStorage
    const userThemePreference = localStorage.getItem('contrast-ui-theme')
    const userPrefersDarkTheme = userThemePreference === 'dark'

    const canApplyDarkMode =
      // eslint-disable-next-line
      userThemePreference != null
        ? userPrefersDarkTheme && isDarkThemeApprovedUrl && isEmbeddedInNS()
        : isDarkThemeApprovedUrl && isEmbeddedInNS()
    $rootScope.canApplyDarkTheme = canApplyDarkMode
    $window.canApplyDarkTheme = canApplyDarkMode
    return canApplyDarkMode
  }

  function defaultStickyPadding() {
    return $rootScope.isEmbeddedInNS
      ? '5px 39px 3px 39px'
      : '16px 39px 12px 39px'
  }

  function loadCustomSettings() {
    CommonFactory.getCustomSettings().then(
      (response) => storeCustomSettings(response.data),
      ErrorService.processError
    )
  }

  function storeCustomSettings(settings) {
    $scope.main.footer = settings.footer
    $scope.main.header = settings.header
    $scope.main.head = settings.head
  }

  function getSecurityStandardReportStatus() {
    return false
  }

  function showReactComponentFromCookie(cookieName) {
    return $cookies.get(cookieName) === 'true'
  }

  function getCurrentOrganization() {
    return $scope.bridgeApi.currentOrganization
  }

  function getAllFeatureTreatments(isSuperadmin: boolean) {
    return isSuperadmin
      ? getAllSuperadminFeatureTreatments()
      : getAllOrgFeatureTreatments()
  }

  function getAllOrgFeatureTreatments() {
    return $http.get(UserService.buildAPIUrl('/feature-flags/treatments'))
  }

  function getAllSuperadminFeatureTreatments() {
    return $http.get(`${TS_BASE_URL}/superadmin/feature-flags/treatments`)
  }

  function initSplitClient(orgUuid) {
    let splitClient

    getAllFeatureTreatments(!orgUuid)
      .then((features) => {
        $rootScope.treatments = features.data
        vanillaSplitClient.getState().setLocalFlags($rootScope.treatments)

        // sending "undefined" as orgUuid for superadmin
        splitClient = buildSplitClient(
          orgUuid ? orgUuid : 'superadmin',
          $rootScope.treatments
        )

        // Wait until the Split client is ready, then assign it to root scope
        splitClient.ready().then(() => {
          $rootScope.splitClient = splitClient
          vanillaSplitClient.getState().setClient(splitClient)

          // Notify that Split treatments have been successfully loaded
          $rootScope.$broadcast('treatments:loaded')
        })
      })
      .catch((error) => {
        $log.error(`Error getting feature treatments: `, error)
      })
  }

  function isRbacServiceOn() {
    const { enabled } = FeaturesService.getRbacStatus()
    return enabled
  }

  function isRbacServiceEnforced() {
    const { enabled, isEnforced } = FeaturesService.getRbacStatus()
    return enabled && isEnforced
  }

  function showAccessControl() {
    return isRbacServiceOn() && permissions.hasRbacManagePermission()
  }

  function showLegacyPermissionUi() {
    return (
      permissions.isEOP() ||
      !isRbacServiceEnforced() ||
      !isFeatureFlagOn('PROD-2573_rbac_view_permissions')
    )
  }

  function showLegacyAuditLogUi() {
    return permissions.isEOP() || !isFeatureFlagOn('PROD-2886_audit_logs_ui')
  }

  function showAuditLog() {
    if (showLegacyAuditLogUi()) {
      return false
    }
    if (isRbacServiceEnforced()) {
      return permissions.hasResourcePermission(
        'ORG_SETTINGS',
        'VIEW_AUDIT_LOGS'
      )
    }
    // EAC
    return permissions.hasPermission('ADMIN')
  }

  function isRbacSsoOn() {
    const { enabled, isRbacSSOProvisioningEnabled } =
      FeaturesService.getRbacStatus()
    return enabled && isRbacSSOProvisioningEnabled
  }

  function showRbacSsoDisplaySettings() {
    return (
      isRbacSsoOn() && isFeatureFlagOn('PROD-3204_RBAC_SSO_Display_settings')
    )
  }

  function hasEnterpriseProtectAccessPermission(eacRole: Role) {
    if (!UserService.isEnterprise()) {
      return false
    }

    return hasProtectAccessPermission(eacRole)
  }

  function hasProtectAccessPermission(eacRole: Role) {
    return isRbacServiceEnforced()
      ? permissions.isRASPEnabled() // internally it checks hasResourcePermission('APPLICATION', 'PROTECT_ACCESS')
      : permissions.hasPermission(eacRole)
  }

  function hasExclusionsPermission(appId: string) {
    return isRbacServiceEnforced()
      ? rbacUserHasExclusionsManage()
      : permissions.hasAppPermission(appId, 'RULES_ADMIN')
  }

  function hasOrganizationExclusionsPermission(eacRole: Role) {
    return isRbacServiceEnforced()
      ? rbacUserHasExclusionsManage()
      : permissions.hasPermission(eacRole)
  }

  function rbacUserHasExclusionsManage() {
    return userHasResourceAction('EXCLUSIONS', 'PROTECT_EXCLUSIONS_MANAGE')
  }

  function hasSensitiveDataPermission() {
    return (
      !isRbacServiceEnforced() ||
      userHasResourceAction(
        'SENSITIVE_DATA',
        'PROTECT_SENSITIVE_DATA_POLICY_MANAGE'
      )
    )
  }

  function hasPoliciesPermission(eacRole: Role, appId: string) {
    return isRbacServiceEnforced()
      ? permissions.hasAppPermission(appId, 'PROTECT_POLICIES_MANAGE')
      : permissions.hasAppPermission(appId, eacRole)
  }

  function hasOrganizationPoliciesPermission() {
    return isRbacServiceEnforced()
      ? userHasResourceAction('APPLICATION', 'PROTECT_POLICIES_MANAGE')
      : permissions.hasPermission('RULES_ADMIN')
  }

  function showUsersAndGroups() {
    return !isFeatureFlagOn('PROD-2415-rbac_service_enforce_assess')
  }

  function showMFABanner() {
    return isFeatureFlagOn('PROD-1881_mfa_banner_enabled')
  }

  function showOrganizationViewSettings() {
    return (
      !permissions.hasPermission('ADMIN') && permissions.hasPermission('EDIT')
    )
  }

  function showOrganizationSettingsPage() {
    return permissions.hasPermission('ADMIN') || showOrganizationViewSettings()
  }

  function showOrganizationSettingsLink() {
    return permissions.hasPermission('ADMIN')
  }

  function showReportsDashboard() {
    const { currentOrganization } = vanillaUserAndOrganizationStore.getState()

    if (!isFeatureFlagOn('PROD-2593_reporting_dashboard')) {
      return false
    }

    const hasAppsOnboarded = currentOrganization.appsOnboarded >= 1

    return isRbacServiceEnforced()
      ? hasAppsOnboarded
      : permissions.hasPermission('ADMIN') && hasAppsOnboarded
  }

  function getIsNavigatingToNewUi() {
    return $scope.isNavigatingToNewUi
  }

  function updateIsNavigatingToNewUi(value: boolean) {
    $scope.isNavigatingToNewUi = value
  }

  function showNavigateToNewUi() {
    return (
      isFeatureFlagOn('PROD-3273_northstar_ui') &&
      isRbacServiceOn() &&
      userHasResourceAction('ORG_SETTINGS', 'INCIDENT_VIEW') &&
      !$scope.isSuperAdmin
    )
  }

  function isAppConfigViewEnabled() {
    return isFeatureFlagOn('PROD-3062_app_config_ui')
  }

  function isFeatureFlagOn(flagName: string) {
    return FeaturesService.isSplitFeatureFlagOn(flagName)
  }

  // TS-31437 - Hide Projects and Add Repo badge based off Split-Flag and RBAC org admin
  function hideScaDataUnlessOrgAdmin() {
    return (
      !isFeatureFlagOn('PROD-2918_hide_sca_data_unless_org_admin') ||
      permissions.hasPermission('ADMIN')
    )
  }

  function isScanMetaDataFlagEnabled() {
    return FeaturesService.isSplitFeatureFlagOn('PROD-2532_scan_metadata')
  }

  function updateCurrentUserStore() {
    const { currentUser } = vanillaUserAndOrganizationStore.getState()
    // Due to a potential race condition, let's be safe and manually provide the treatments
    const { enabled, isEnforced, isScanEnforced } =
      FeaturesService.getRbacStatus($rootScope.treatments)
    const isRbacEnforced = enabled && isEnforced

    permissions.setRbacAttackEventsViewPermission()

    // loads the PLATFORM_ORG_MANAGE permission in preview
    if (enabled && !isEnforced) {
      permissions.setRbacManagePermission()
    }

    // Check if RBAC is enforced for Scan and load project create permission
    if (enabled && isScanEnforced) {
      permissions.setSastProjectCreatePermission()
    }

    if (enabled && !$scope.areUserActionsLoaded) {
      if ($scope.areUserActionsLoaded === null) {
        $log.debug('User actions are still loading, be patient...')
      } else {
        // use null to signify that the user actions are being loaded
        $scope.areUserActionsLoaded = null

        permissions
          .getUserActions()
          .then((data) => {
            const { actions, resourceAccessObj } = data
            const resourceAccessMap =
              permissions.addSastProjectCreatePermissionToResourceActionMap(
                resourceAccessObj
              )

            // If RBAC is enforced, set the permissions and resource actions
            if (isRbacEnforced) {
              permissions.setPermissions(actions)
              permissions.setResourceActions(resourceAccessMap)
            }

            vanillaUserAndOrganizationStore.getState().setCurrentUser(
              mapProfileToUser({
                userActions: isRbacEnforced ? actions : currentUser.roles,
                userAccessObj: resourceAccessMap
              })
            )
            $scope.areUserActionsLoaded = true
          })
          .catch((error) => {
            $log.error(`Error retrieving access control data: `, error)
          })
      }
    } else {
      const resourceAccessMap =
        permissions.addSastProjectCreatePermissionToResourceActionMap(
          currentUser.accessList
        )

      /**
       * If the user has access list items assigned then it's safe to
       * assume the roles are actions and we want to use those instead
       */
      vanillaUserAndOrganizationStore.getState().setCurrentUser(
        mapProfileToUser({
          userActions: currentUser.roles,
          userAccessObj: resourceAccessMap
        })
      )
      $scope.areUserActionsLoaded = true
    }
  }

  // reload user profile [settings > profile]
  $scope.$on('profile:updated', () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    permissions.getUser()
    if ($scope.areTreatmentsLoaded) {
      updateCurrentUserStore()
    }
  })
  $scope.$on(ACCESS_EVENTS.ACCESS_RELOAD, () => {
    permissions.getUserAccess(true)

    if ($scope.areTreatmentsLoaded) {
      updateCurrentUserStore()
    }
  })

  $scope.$on(ORGANIZATION_SWITCH_EVENT, () => ($scope.isOrgBeingChanged = true))

  // after reloading user profile
  $scope.$on('profile:loaded', () => {
    $scope.main.name = ProfileService.getFirstName()
    $scope.main.timeFormat = ProfileService.getTimeFormat()
    $scope.main.dateFormat = ProfileService.getDateFormat()
    $scope.main.dateTimeFormat = ProfileService.getDateTimeFormat()
    $scope.main.timezone = ProfileService.getTimezone()
    $scope.main.uid = ProfileService.getUid()
    $scope.main.managingUsers = GlobalService.isManagingUsers()
    $scope.main.timezoneOffset = TimeService.getTimezoneOffsetString()

    // Populate currentUser with default API response or use the org UUID
    // to fetch user actions and update current user if RBAC is enabled
    if ($scope.areTreatmentsLoaded) {
      updateCurrentUserStore()
    }
  })
  $scope.$on('isManagingUsers:loaded', () => {
    $scope.main.managingUsers = GlobalService.isManagingUsers()
  })

  $scope.$on('permissions:updated', () => {
    if ($scope.areTreatmentsLoaded) {
      updateCurrentUserStore()
    }
  })

  // reload roles
  $scope.$on('roles:updated', () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    permissions.getRoles()
  })
  // destroy client side session
  $scope.$on(AUTH_EVENTS.UNAUTHENTICATED_EVENT, () => {
    AuthService.setAuthenticated(false)
    vanillaUserAndOrganizationStore.getState().setIsAuthenticated(false)
  })
  // update views
  $scope.$on(AUTH_EVENTS.AUTHENTICATED_TRUE, () => {
    _authenticated = true
    vanillaUserAndOrganizationStore.getState().setIsAuthenticated(true)
  })
  $scope.$on(AUTH_EVENTS.AUTHENTICATED_FALSE, () => {
    _authenticated = false
    vanillaUserAndOrganizationStore.getState().setIsAuthenticated(false)
  })
  // set current organization
  $scope.$on('organization:loaded', (_, data) => {
    // Until every org is using new RBAC we are going to force a page reload
    // To prevent race conditions with split flags getting set improperly for
    // the org the user is switching to
    if ($scope.isOrgBeingChanged) {
      $window.location.reload()
    }
    const currentOrganization = normalizeOrganization(data)

    $scope.main.currentOrg = data.organization_uuid
    $scope.main.orgName = data.getFirstName
    $scope.isHarmonyEnabled = data.harmony_enabled
    $scope.isSastEnabled = data.sast_enabled
    $scope.isCloudnativeEnabled = data.cloudnative_enabled
    $scope.allowEnableImpersonation =
      data.properties && data.properties.allow_enable_impersonation === 'true'
        ? true
        : false
    // @ts-ignore
    $scope.bridgeApi.currentOrganization = currentOrganization
    $rootScope.currentOrganization = currentOrganization
    $scope.areUserActionsLoaded = false
    vanillaUserAndOrganizationStore
      .getState()
      .setCurrentOrganization(currentOrganization)

    initSplitClient(data.organization_uuid)

    ProfileFactory.getOrgDiagnosticsSettings($scope.main.currentOrg).then(
      (response) => {
        $scope.isDiagnosticsEnabled =
          response?.data?.settings?.diagnostics_collection_enabled
      }
    )
  })

  $scope.$on('treatments:loaded', () => {
    $scope.areTreatmentsLoaded = true
    updateCurrentUserStore()
  })

  $scope.$on('superadmin:roles:loaded', () => {
    $scope.isSuperAdmin = true
    initSplitClient(undefined)
  })

  $scope.$on('featureFlags:loaded', (_, data) => {
    $scope.main.featureFlags = data

    vanillaFeatureFlags.getState().setFeatureFlags($scope.main.featureFlags)
  })
  $scope.$on('agentFeatures:loaded', (_, data) => {
    $scope.main.agentFeatures = data
  })
  // reload organizations
  $scope.$on('eacgroups:updated', () => permissions.getAllowedOrganizations())

  // timeout detected
  $scope.$on(TIMEOUT_EVENT, () => AuthService.isUserActive())

  $scope.$watch(
    'admin',
    (newVal: any, oldVal: any) => {
      if (newVal.menu === 'horizontal' && oldVal.menu === 'vertical') {
        $rootScope.$broadcast('nav:reset')
        return
      }
      if (newVal.fixedHeader === false && newVal.fixedSidebar === true) {
        if (oldVal.fixedHeader === false && oldVal.fixedSidebar === false) {
          $scope.admin.fixedHeader = true
          $scope.admin.fixedSidebar = true
        }
        if (oldVal.fixedHeader === true && oldVal.fixedSidebar === true) {
          $scope.admin.fixedHeader = false
          $scope.admin.fixedSidebar = false
        }
        return
      }
      if (newVal.fixedSidebar === true) {
        $scope.admin.fixedHeader = true
      }
      if (newVal.fixedHeader === false) {
        $scope.admin.fixedSidebar = false
      }
    },
    true
  )

  init()
}
